import { InjectedConnector } from '@web3-react/injected-connector'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { WalletConnectConnector } from '@web3-react/walletconnect-connector'
import { Web3Provider } from '@ethersproject/providers'
import { DeFiWeb3Connector } from '@deficonnect/web3-connector'
import { BscConnector } from '@binance-chain/bsc-connector'
import { ConnectorNames } from 'vvs-uikit'
import { ethers } from 'ethers'
import getNodeUrl from './getRpcUrl'

const POLLING_INTERVAL = 12000
const rpcUrl = getNodeUrl()
const chainId = parseInt(process.env.REACT_APP_CHAIN_ID, 10)

const injected = new InjectedConnector({ supportedChainIds: [chainId] })

const walletconnect = new WalletConnectConnector({
  rpc: { [chainId]: rpcUrl },
  chainId,
  supportedChainIds: [chainId],
  qrcode: true,
  pollingInterval: POLLING_INTERVAL,
})

const defiWalletConnect = new DeFiWeb3Connector({
  supportedChainIds: [chainId],
  rpcUrls: { [chainId]: rpcUrl },
  chainType: 'eth',
  chainId: `${chainId}`,
  appName: 'VVS Finance',
})

const bscConnector = new BscConnector({ supportedChainIds: [chainId] })

export const connectorsByName: { [connectorName in ConnectorNames]: any } = {
  [ConnectorNames.Injected]: injected,
  [ConnectorNames.Metamask]: injected,
  [ConnectorNames.TrustWallet]: injected,
  [ConnectorNames.WalletConnect]: walletconnect,
  [ConnectorNames.BSC]: bscConnector,
  [ConnectorNames.CDCDefiWallet]: defiWalletConnect,
  [ConnectorNames.CDCDesktopWallet]: 'https://crypto.org/desktopwallet',
  [ConnectorNames.BraveWallet]: injected,
}

export const getLibrary = (provider): ethers.providers.Web3Provider => {
  const library = new ethers.providers.Web3Provider(provider)
  library.pollingInterval = POLLING_INTERVAL
  return library
}

export const signMessage = async (
  provider: Web3Provider,
  account: string,
  message: string,
  connector?: AbstractConnector,
): Promise<string> => {
  /**
   * BSC Wallet requires a different sign method
   * @see https://docs.binance.org/smart-chain/wallet/wallet_api.html#binancechainbnbsignaddress-string-message-string-promisepublickey-string-signature-string
   */
  if (window.BinanceChain && connector === bscConnector) {
    const { signature } = await window.BinanceChain.bnbSign(account, message)
    return signature
  }

  const rawSignedMessage = await signMessageWithProvider(provider, account, message)
  return normalizeSignature(rawSignedMessage)
}

const signMessageWithProvider = (provider: Web3Provider, account: string, message: string): Promise<string> => {
  /**
   * For CDC DefiWallet
   * Wallet Connect does not sign the message correctly unless you use their method
   * @see https://github.com/WalletConnect/walletconnect-monorepo/issues/462
   */
  const wcProvider = provider.provider as any
  if (wcProvider?.wc) {
    const wcMessage = ethers.utils.hexlify(ethers.utils.toUtf8Bytes(message))

    return wcProvider?.wc?.signPersonalMessage
      ? wcProvider.wc.signPersonalMessage([wcMessage, account])
      : provider.send('personal_sign', [wcMessage, account])
  }

  return provider.getSigner(account).signMessage(message)
}

/**
 * some hardware cold wallet can generate signatures that are not verifiable on BE side
 * normalizing by split and join can solve the problem
 * https://docs.ethers.io/v5/api/utils/bytes/#byte-manipulation--signature-conversion
 */
const normalizeSignature = (signature: string) => ethers.utils.joinSignature(ethers.utils.splitSignature(signature))
